"use client";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import useEmblaCarousel from "embla-carousel-react";
import ComingSoonCard from "./coming-soon-card";
import { ButtonSize, IconButton } from "@/stories/icon-button/icon-button";
import ArrowLeft from "@/app/assets/icons/arrow-left";
import { ButtonVariant } from "@/stories/button/button";
import { getUEListData } from "@/app/api/service/homepage";
import { UEListData } from "@/app/interface/homepage";
import { ListingCategory } from "@/app/enums/unlisted";
import { ALLOWED_SHARE_STATES } from "@/app/utils/product-visibility";

interface ComingSoonProps {
    initialData?: { data: UEListData[]; success: boolean } | null;
}

const ComingSoon: React.FC<ComingSoonProps> = ({ initialData }) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({
        align: "center",
    });
    const [currIdx, setCurrIdx] = useState(0);

    const { data: ueData } = useQuery({
        queryKey: ["ueListData"],
        queryFn: () => getUEListData(),
        initialData: initialData ?? undefined, // Use SSR data for initial render (SEO)
    });

    const filteredData = useMemo(() => {
        if (!ueData?.success) return [];

        return ueData.data.filter(
            (item: UEListData) =>
                item?.product &&
                item?.ISIN?.includes("BASKET") === false &&
                item?.company?.logoUrl?.length > 20 &&
                item?.category === ListingCategory.UPCOMING &&
                ALLOWED_SHARE_STATES.has(item?.shareState?.toLowerCase().trim() || "")
        );
    }, [ueData]);

    const scrollPrev = useCallback(() => {
        if (emblaApi) emblaApi.scrollPrev();
    }, [emblaApi]);

    const scrollNext = useCallback(() => {
        if (emblaApi) emblaApi.scrollNext();
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        const onSelect = () => setCurrIdx(emblaApi.selectedScrollSnap());
        emblaApi.on("select", onSelect);
        return () => {
            emblaApi.off("select", onSelect);
        };
    }, [emblaApi]);

    if (filteredData.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-col py-s15 md:py-s17 md:mb-5">
            <div className="mb-s9 md:mb-s14">
                <h2 className="text-center text-4xl font-semibold md:text-7xl">Upcoming Unlisted Shares</h2>
                <p className="text-center text-md md:text-lg">
                    Popular Pre - IPO || Unlisted Shares that we are currently evaluating
                </p>
            </div>

            {/* Embla Carousel for md and above */}
            <div className="hidden items-center justify-center gap-s9 md:flex md:justify-between">
                <IconButton
                    icon={<ArrowLeft />}
                    size={ButtonSize.Large}
                    variant={ButtonVariant.SecondaryDefault}
                    className={"max-md:hidden"}
                    onClick={scrollPrev}
                    disabled={currIdx === 0}
                />
                <div ref={emblaRef} className="overflow-hidden md:w-[1020px]">
                    <div className="flex gap-s6">
                        {filteredData?.map(
                            (card: UEListData, index: number, array: UEListData[]) => (
                                <div
                                    key={index}
                                    className={`${index === array.length - 1 && "mr-s6"}`}
                                >
                                    <ComingSoonCard {...card} />
                                </div>
                            ),
                        )}
                    </div>
                </div>
                <IconButton
                    icon={<ArrowLeft className="rotate-180" />}
                    size={ButtonSize.Large}
                    variant={ButtonVariant.SecondaryDefault}
                    className={"max-md:hidden"}
                    onClick={scrollNext}
                    disabled={currIdx === filteredData?.length - 3}
                />
            </div>

            {/* Vertical Cards for small screens */}
            <div className="block md:hidden">
                <div className="flex flex-col items-center justify-center gap-s6">
                    {filteredData?.map(
                        (card: UEListData, index: number, array: UEListData[]) => {
                            if (index < array.length - 1) {
                                return (
                                    <div key={index}>
                                        <ComingSoonCard {...card} />
                                    </div>
                                );
                            }
                        },
                    )}
                </div>
            </div>
        </div>
    );
};

export default ComingSoon;